import axios, { AxiosInstance } from 'axios';
import { MonitoringEndpoint } from '../types';

export class MonitoringService {
  private client: AxiosInstance;

  constructor(baseUrl: string = 'http://localhost:8080/api') {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Get all monitoring endpoints
  async getMonitoringEndpoints(
    namespace?: string,
  ): Promise<MonitoringEndpoint[]> {
    try {
      const url = namespace
        ? `/v1/namespaces/${namespace}/monitoring-instances`
        : `/v1/monitoring-instances`;
      const response = await this.client.get(url);
      return response.data;
    } catch (_error) {
      return this.getMockMonitoringEndpoints();
    }
  }

  // Get single monitoring endpoint
  async getMonitoringEndpoint(
    namespace: string,
    name: string,
  ): Promise<MonitoringEndpoint> {
    try {
      const response = await this.client.get(
        `/v1/namespaces/${namespace}/monitoring-instances/${name}`,
      );
      return response.data;
    } catch (_error) {
      return this.getMockMonitoringEndpoints().find(
        m => m.metadata.name === name,
      )!;
    }
  }

  // Create monitoring endpoint
  async createMonitoringEndpoint(
    endpoint: MonitoringEndpoint,
  ): Promise<MonitoringEndpoint> {
    try {
      const response = await this.client.post(
        `/v1/namespaces/${endpoint.metadata.namespace}/monitoring-instances`,
        endpoint,
      );
      return response.data;
    } catch (_error) {
      return endpoint;
    }
  }

  // Update monitoring endpoint
  async updateMonitoringEndpoint(
    namespace: string,
    name: string,
    endpoint: Partial<MonitoringEndpoint>,
  ): Promise<MonitoringEndpoint> {
    try {
      const response = await this.client.put(
        `/v1/namespaces/${namespace}/monitoring-instances/${name}`,
        endpoint,
      );
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to update monitoring endpoint '${namespace}/${name}' failed`,
        _error,
      );
      return this.getMockMonitoringEndpoints()[0];
    }
  }

  // Delete monitoring endpoint
  async deleteMonitoringEndpoint(
    namespace: string,
    name: string,
  ): Promise<void> {
    try {
      await this.client.delete(
        `/v1/namespaces/${namespace}/monitoring-instances/${name}`,
      );
    } catch (_error) {
      console.log(`Mock: Deleted monitoring endpoint ${namespace}/${name}`);
    }
  }

  // Test monitoring endpoint connection
  async testMonitoringConnection(
    endpoint: MonitoringEndpoint,
  ): Promise<boolean> {
    try {
      const response = await this.client.post(
        `/v1/monitoring-instances/test`,
        endpoint,
      );
      return response.data.success;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to test monitoring connection failed',
        _error,
      );
      return true; // Mock: connection successful
    }
  }

  // Mock data helpers
  private getMockMonitoringEndpoints(): MonitoringEndpoint[] {
    return [
      {
        metadata: {
          name: 'pmm-monitoring',
          namespace: 'everest-system',
        },
        spec: {
          type: 'pmm',
          url: 'https://pmm.example.com',
          allowedNamespaces: ['everest', 'production'],
          verifyTLS: true,
          credentials: {
            username: 'admin',
            password: '***',
          },
        },
        status: {
          usedBy: ['pg-production', 'mongo-analytics'],
        },
      },
      {
        metadata: {
          name: 'pmm-dev',
          namespace: 'everest-system',
        },
        spec: {
          type: 'pmm',
          url: 'http://pmm-dev.local:8080',
          allowedNamespaces: ['everest'],
          verifyTLS: false,
          credentials: {
            username: 'admin',
            password: '***',
          },
        },
        status: {
          usedBy: [],
        },
      },
    ];
  }
}

export const monitoringService = new MonitoringService();
